<template>
  <div class="daily-page">
    <DailyClaimBanner :amount="claimableAmount" :loading="claiming" @claim="onClaimAll" />
    <DailyTaskGroup
      v-for="(task, idx) in tasks"
      :key="task.id ?? idx"
      :task="task"
      @action="level => onLevelAction(task, level)"
    />
    <Transition name="toast-fade">
      <div v-if="toastMessage" class="daily-toast">{{ toastMessage }}</div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { claimAllTasks, claimTaskStage, fetchTaskPanel } from '~/api/promo'
import { formatBaseAmt } from '~/utils/transfer/money'
import type { DailyTask, DailyTaskLevel } from './types'
import type {
  TaskClaimResultDto,
  TaskStageAction,
  TaskStageDto,
  TaskStageStatus,
  TaskViewDto,
} from '~/types/api/promo'
import DailyClaimBanner from './components/DailyClaimBanner.vue'
import DailyTaskGroup from './components/DailyTaskGroup.vue'

const userStore = useUserStore()
const { t } = useI18n()
const { load: loadOverview } = useRewardOverview()
const { refreshWallet } = useWallet()
const fmt = (n: number) => formatBaseAmt(n, userStore.baseCurrency)

const LEVEL_STATE_MAP: Record<TaskStageStatus, DailyTaskLevel['state']> = {
  LOCKED: 'locked',
  IN_PROGRESS: 'progress',
  CLAIMABLE: 'claimable',
  PROCESSING: 'progress',
  RISK_HOLD: 'locked',
  RISK_CHALLENGE: 'locked',
  CLAIMED: 'claimed',
  REJECTED: 'locked',
  EXPIRED: 'locked',
}

const toNumber = (value: string) => Number(value) || 0

const resolveMetric = (type: TaskViewDto['type']): DailyTask['metric'] => {
  if (type === 'DEPOSIT') return 'deposit'
  if (type === 'BET_TURNOVER') return 'bet'
  return 'count'
}

const mapStage = (stage: TaskStageDto): DailyTaskLevel => ({
  label: t('promo.daily.stageLabel', { no: stage.stage_no }),
  stageNo: stage.stage_no,
  requirement: toNumber(stage.threshold_amount),
  reward: toNumber(stage.reward_amount),
  state: LEVEL_STATE_MAP[stage.status],
  action: stage.action,
  claimId: stage.claim_id,
})

const mapTask = (task: TaskViewDto): DailyTask => ({
  id: task.task_code,
  iconUrl: task.icon_url?.trim() || undefined,
  iconKey: task.icon_key?.trim() || undefined,
  title: task.title,
  tag: task.tag,
  rule: task.subtitle,
  currentBet: toNumber(task.current_amount),
  nextTarget: toNumber(task.next_target),
  progressPct: task.progress_pct == null ? undefined : toNumber(task.progress_pct),
  metric: resolveMetric(task.type),
  type: task.type,
  levels: task.stages.map(mapStage),
})

const { data: panel, refresh } = await useAsyncData('rewards-tasks', () => fetchTaskPanel())

/** 领取后：面板（进度/下一关/各关状态）+ 页签角标 + 钱包余额一起重拉 */
const refreshAfterClaim = () => {
  void Promise.all([refresh(), loadOverview(), refreshWallet()]).catch(error => {
    console.error('Refresh after claim failed:', error)
  })
}

const tasks = ref<DailyTask[]>([])

watch(
  () => panel.value,
  val => {
    if (val) tasks.value = val.tasks.map(mapTask)
  },
  { immediate: true },
)

const claimableAmount = computed(() =>
  tasks.value
    .flatMap(task => task.levels)
    .filter(level => level.state === 'claimable' && !level.claimId)
    .reduce((sum, level) => sum + level.reward, 0),
)

const toastMessage = ref('')
let toastTimer: ReturnType<typeof setTimeout> | null = null
const showToast = (message: string) => {
  if (toastTimer) clearTimeout(toastTimer)
  toastMessage.value = message
  toastTimer = setTimeout(() => {
    toastMessage.value = ''
  }, 2000)
}

const toDailyState = (
  status: TaskClaimResultDto['status'],
  action: TaskStageAction,
): DailyTaskLevel['state'] => {
  if (status === 'CLAIMED') return 'claimed'
  if (action === 'CLAIM') return 'claimable'
  return 'progress'
}

const applyResult = (result: TaskClaimResultDto) => {
  const task = tasks.value.find(t => t.id === result.task_code)
  const level = task?.levels.find(l => l.stageNo === result.stage_no)
  if (!level) return
  level.action = result.action
  level.claimId = result.claim_id
  level.state = toDailyState(result.status, result.action)
}

const claiming = ref(false)
/** 单关领取在途锁：同一关卡在请求返回前重复点击不重复发请求 */
const busyStages = new Set<string>()

const onClaimAll = async () => {
  const amount = claimableAmount.value
  if (amount <= 0 || claiming.value) return
  claiming.value = true
  try {
    const result = await claimAllTasks()
    result.results.forEach(applyResult)
    refreshAfterClaim()
    const claimed = Number(result.claimed_amount) || 0
    const message = result.partial
      ? t('promo.daily.claimAllPartial', { amount: fmt(claimed) })
      : t('promo.daily.claimAll', { amount: fmt(claimed) })
    showToast(message)
  } catch (error) {
    console.error('Claim all tasks failed:', error)
  } finally {
    claiming.value = false
  }
}

const onLevelAction = async (task: DailyTask, level: DailyTaskLevel) => {
  if (level.action !== 'CLAIM') return
  const busyKey = `${task.id}:${level.stageNo}`
  if (claiming.value || busyStages.has(busyKey)) return
  busyStages.add(busyKey)
  try {
    const result = await claimTaskStage({ taskCode: task.id, stageNo: level.stageNo })
    applyResult(result)
    refreshAfterClaim()
    const amount = Number(result.amount) || level.reward
    const message =
      result.status === 'CLAIMED'
        ? t('promo.daily.claimLevelClaimed', { amount: fmt(amount) })
        : result.status === 'PROCESSING'
          ? t('promo.daily.claimLevelProcessing', { amount: fmt(amount) })
          : result.status === 'RISK_CHALLENGE'
            ? t('promo.daily.claimLevelChallenge')
            : t('promo.daily.claimLevelPending')
    showToast(message)
  } catch (error) {
    console.error('Claim task stage failed:', error)
  } finally {
    busyStages.delete(busyKey)
  }
}
</script>

<style scoped lang="scss">
.daily-page {
  display: block;
  animation: fade 0.2s ease;
}

.daily-toast {
  position: fixed;
  left: 50%;
  bottom: calc(var(--tabbar-h) + 24px + env(safe-area-inset-bottom, 0px));
  transform: translateX(-50%);
  z-index: 80;
  padding: 10px 18px;
  border-radius: var(--r-pill);
  background: rgba(20, 18, 30, 0.92);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  box-shadow: var(--shadow);
  white-space: nowrap;
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(6px);
}
</style>
